import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Youtube, Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COL_LINKS = [
  {
    title: "Explore",
    links: [
      { to: "/flights", label: "Flights" },
      { to: "/hotels", label: "Hotels" },
      { to: "/packages", label: "Holiday Packages" },
      { to: "/deals", label: "Travel Deals" },
      { to: "/destinations", label: "Destinations" },
    ],
  },
  {
    title: "Guides",
    links: [
      { to: "/visa-guides", label: "Visa Guides" },
      { to: "/blog", label: "Travel Blog" },
      { to: "/blog?category=Travel Tips", label: "Travel Tips" },
      { to: "/blog?category=Hotel Reviews", label: "Hotel Reviews" },
      { to: "/blog?category=Airline Reviews", label: "Airline Reviews" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/contact", label: "Contact" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/affiliate-disclosure", label: "Affiliate Disclosure" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed successfully");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300 mt-24">
      <div className="container-page py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand text-white font-montserrat font-extrabold text-lg">M</span>
              <div>
                <div className="font-montserrat font-extrabold text-white text-lg">MyTripPoint</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400">Your Journey Starts Here</div>
              </div>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 max-w-md">
              MyTripPoint helps Indian and international travellers compare flights, hotels and travel deals from leading partners — and book with confidence.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center gap-2"><Mail size={16} className="text-accent" /> hello@mytrippoint.com</div>
              <div className="flex items-center gap-2"><Phone size={16} className="text-accent" /> +91 80 4567 8901</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-accent" /> Bengaluru, India</div>
            </div>
            <div className="flex gap-3 mt-5">
              {[Facebook, Twitter, Instagram, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`social-${i}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-slate-800 hover:bg-brand transition-colors"
                  aria-label="social"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {COL_LINKS.map((col) => (
            <div key={col.title}>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">{col.title}</div>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="font-montserrat font-bold text-white text-lg">Get travel deals in your inbox</div>
            <div className="text-sm text-slate-400 mt-1">Weekly handpicked flight & hotel deals. No spam, unsubscribe anytime.</div>
          </div>
          <form onSubmit={submit} className="flex gap-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="footer-newsletter-email"
              className="flex-1 h-11 px-4 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="h-11 px-5 rounded-lg btn-accent font-semibold disabled:opacity-60"
            >
              {busy ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="mt-8 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-2 justify-between">
          <div>© {new Date().getFullYear()} MyTripPoint. All rights reserved.</div>
          <div className="flex gap-3">
            <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-300">Terms</Link>
            <Link to="/affiliate-disclosure" className="hover:text-slate-300">Affiliate Disclosure</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
